import DocumentationSidebarTreeFolder from './DocumentationSidebarTreeFolder'
import {trlObject} from '~/services/intl'

import type {IntlMap} from '~/intl/ru'

export type TopicItem = {
    root?: string
    isDynamicPage?: boolean
    items?: TopicItem[]
    type?: 'online' | 'offline'
    name?: string | undefined
    path?: keyof IntlMap['documentation'] | undefined
}

export default function() {
    const documentationTopics: TopicItem[] = [
        {
            name: trlObject('documentation').intro,
            items: [
                {
                    path: 'recognition',
                    items: [
                        {
                            path: 'technology',
                            root: 'recognition',
                        },
                        {
                            path: 'specifications',
                            root: 'recognition',
                        },
                    ],
                },
                {
                    path: 'synthesis',
                    items: [
                        {
                            path: 'technology',
                            root: 'synthesis',
                        },
                        {
                            path: 'voices',
                            root: 'synthesis',
                            isDynamicPage: true,
                        },
                        {
                            path: 'normalization',
                            root: 'synthesis',
                        },
                        {
                            path: 'pronunciation',
                            root: 'synthesis',
                            isDynamicPage: true,
                        },
                    ],
                },
            ],
        }, {
            name: trlObject('documentation').api,
            items: [
                {
                    name: trlObject('documentation').start,
                    items: [
                        {
                            path: 'post-token',
                            root: 'oauth',
                            isDynamicPage: true,
                        },
                        {
                            path: 'get-profile',
                            root: 'profile',
                            isDynamicPage: true,
                        },
                    ],
                },
                {
                    name: 'gRPC',
                    items: [
                        {
                            path: 'recognition-api',
                            root: 'recognition',
                            items: [
                                {path: 'recognition-supported-models'},
                                {
                                    path: 'flow-recognition',
                                    type: 'online',
                                },
                                {
                                    path: 'file-recognition',
                                    type: 'offline',
                                },
                            ],
                        },
                        {
                            path: 'synthesis-api',
                            root: 'synthesis',
                            items: [
                                {path: 'synthesis-supported-models'},
                                {
                                    path: 'synthesis',
                                    type: 'offline',
                                },
                                {
                                    path: 'flow-speech-synthesis',
                                    type: 'online',
                                },
                                {path: 'synthesis-errors'},
                            ],
                        },
                        {
                            path: 'pre-records-api',
                            root: 'pre-records',
                            items: [
                                {path: 'pre-records-get-list'},
                                {path: 'pre-records-add-record'},
                                {path: 'pre-records-update-record'},
                                {path: 'pre-records-get-record'},
                                {path: 'pre-records-rename-record'},
                                {path: 'pre-records-delete-record'},
                                {path: 'pre-records-errors'},
                            ],
                        },
                        {
                            path: 'noises-api',
                            root: 'noises',
                            items: [
                                {path: 'noises-get-list'},
                                {path: 'noises-add-record'},
                                {path: 'noises-update-record'},
                                {path: 'noises-get-record'},
                                {path: 'noises-delete-record'},
                                {path: 'noises-errors'},
                            ],
                        },
                        {
                            path: 'dictionary-api',
                            root: 'dictionary',
                            items: [
                                {path: 'get-dictionary-list'},
                                {path: 'add-dictionary'},
                                {path: 'load-dictionary'},
                                {path: 'add-words'},
                                {path: 'get-words-list'},
                                {path: 'get-words'},
                                {path: 'delete-words'},
                                {path: 'delete-dictionary'},
                                {path: 'reload-dictionary'},
                                {path: 'dictionary-error'},
                            ],
                        },
                        {
                            path: 'metrics-api',
                            root: 'metrics',
                            items: [
                                {path: 'get-metrics'},
                                {path: 'metrics-get-node-info'},
                                {path: 'metrics-errors'},
                            ],
                        },
                    ],
                },
                {
                    name: 'REST',
                    items: [
                        {
                            path: 'recognition-api',
                            items: [
                                {
                                    path: 'get-asr-models',
                                    root: 'asr',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'get-asr-tasks',
                                    root: 'asr',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'post-asr-tasks',
                                    root: 'asr',
                                    isDynamicPage: true,
                                    type: 'offline',
                                },
                                {
                                    path: 'get-asr-tasks-task_id',
                                    root: 'asr',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'post-asr-tasks-search',
                                    root: 'asr',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'delete-asr-tasks-task_id',
                                    root: 'asr',
                                    isDynamicPage: true,
                                },
                            ],
                        },

                        {
                            path: 'tts',
                            root: 'synthesis',
                            items: [
                                {
                                    path: 'get-tts-voices',
                                    root: 'tts',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'get-tts-noises',
                                    root: 'tts',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'post-tts-synthesize',
                                    root: 'tts',
                                    isDynamicPage: true,
                                    type: 'offline',
                                },
                            ],
                        },

                        {
                            path: 'speakers-api',
                            items: [
                                {
                                    path: 'get-speakers-models',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'post-speakers-models',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'get-speakers-models-model_id',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'delete-speakers-models-model_id',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'get-speakers-tasks',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'post-speakers-tasks',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'get-speakers-tasks-task_id',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'delete-speakers-tasks-task_id',
                                    root: 'speakers',
                                    isDynamicPage: true,
                                },
                            ],
                        },
                        {
                            path: 'storage-api',
                            items: [
                                {
                                    path: 'get-storage-files',
                                    root: 'storage',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'post-storage-files',
                                    root: 'storage',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'get-storage-files-file_id',
                                    root: 'storage',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'patch-storage-files-file_id',
                                    root: 'storage',
                                    isDynamicPage: true,
                                },
                                {
                                    path: 'delete-storage-files-file_id',
                                    root: 'storage',
                                    isDynamicPage: true,
                                },
                            ],
                        },
                    ],
                },
            ],
        },

    ]

    return <div className='documentation-menu documentation-menu__wrapper'>
        {documentationTopics.map((item, index) =>
            <DocumentationSidebarTreeFolder
                key={index}
                item={item}
            />
        )}
    </div>
}
